import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../api';
import { useTenant } from '../contexts/TenantContext';
import { moduleEnabled } from '../config/modules';
import {
  QrCode, Camera, CheckCircle, XCircle, RefreshCw, MapPin,
  Calendar, Clock, Bus, Package, PenLine, Trash2, Users, Search, CreditCard
} from 'lucide-react';

// ─── QR Scanner ────────────────────────────────────────────────────────────
let _scannerCleanup = Promise.resolve();

function QrScanner({ onScan, onError, hintText }) {
  const [cameraLoading, setCameraLoading] = useState(true);

  useEffect(() => {
    const ctx = { scanner: null, started: false, active: true };

    (async () => {
      try { await _scannerCleanup; } catch (_) {}
      if (!ctx.active) return;
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        ctx.scanner = new Html5Qrcode('qr-reader-container');
        await ctx.scanner.start(
          { facingMode: 'environment' },
          { fps: 15, qrbox: { width: 260, height: 260 } },
          (decoded) => {
            if (ctx.active) {
              ctx.active = false;
              const s = ctx.scanner;
              ctx.scanner = null;
              s.stop().catch(() => {});
              onScan(decoded);
            }
          },
          () => {}
        );
        ctx.started = true;
        if (ctx.active) {
          setCameraLoading(false);
        } else {
          const s = ctx.scanner;
          ctx.scanner = null;
          s?.stop().catch(() => {});
        }
      } catch (err) {
        if (ctx.active) onError(err?.message || 'Não foi possível acessar a câmera');
      }
    })();

    return () => {
      ctx.active = false;
      const s = ctx.scanner;
      ctx.scanner = null;
      if (s) {
        if (ctx.started) {
          _scannerCleanup = s.stop().catch(() => {}).finally(() => { try { s.clear(); } catch (_) {} });
        } else {
          _scannerCleanup = new Promise((resolve) => { try { s.clear(); } catch (_) {} resolve(); });
        }
      }
    };
  }, []); // eslint-disable-line

  return (
    <div className="flex flex-col items-center w-full">
      <div className="relative w-full max-w-sm shadow-lg rounded-xl" style={{ minHeight: 280 }}>
        {cameraLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-10 rounded-xl border-2 border-orange-400">
            <div className="w-10 h-10 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin mb-3" />
            <p className="text-slate-700 text-sm font-semibold">Inicializando câmera...</p>
            <p className="text-slate-400 text-xs mt-1">Aguarde alguns segundos</p>
          </div>
        )}
        <div id="qr-reader-container" className="w-full rounded-xl overflow-hidden border-2 border-orange-400" style={{ minHeight: 280 }} />
      </div>
      <p className="text-slate-500 text-sm mt-3 text-center px-4">
        {cameraLoading ? 'Solicitando permissão de câmera...' : (hintText || 'Aponte a câmera para o QR Code')}
      </p>
    </div>
  );
}

// ─── Signature Canvas ──────────────────────────────────────────────────────
function SignatureCanvas({ onSave, onSkip }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasStrokes, setHasStrokes] = useState(false);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };
  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };
  const draw = (e) => {
    e.preventDefault();
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0f172a';
    const pos = getPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasStrokes(true);
  };
  const end = (e) => { e.preventDefault(); drawing.current = false; };
  const clear = () => {
    canvasRef.current.getContext('2d').clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasStrokes(false);
  };
  const save = () => {
    if (!hasStrokes) { onSkip(); return; }
    onSave(canvasRef.current.toDataURL('image/png'));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PenLine className="w-4 h-4 text-orange-500" />
          <p className="text-sm font-semibold text-slate-700">Assinatura do recebedor</p>
        </div>
        <button onClick={clear} className="flex items-center gap-1 text-xs text-slate-400 hover:text-red-500 transition-colors">
          <Trash2 className="w-3 h-3" /> Limpar
        </button>
      </div>
      <div className="relative border-2 border-dashed border-slate-300 rounded-xl overflow-hidden bg-slate-50">
        <canvas ref={canvasRef} width={380} height={140} className="w-full touch-none cursor-crosshair"
          onMouseDown={start} onMouseMove={draw} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={draw} onTouchEnd={end} />
        {!hasStrokes && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-slate-300 text-sm">Assine aqui</p>
          </div>
        )}
      </div>
      <div className="flex gap-3">
        <button onClick={onSkip}
          className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors">
          Pular assinatura
        </button>
        <button onClick={save}
          className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-2.5 rounded-xl text-sm transition-colors">
          {hasStrokes ? 'Usar esta assinatura' : 'Confirmar sem assinatura'}
        </button>
      </div>
    </div>
  );
}

// ─── Resultado scan — Embarque ─────────────────────────────────────────────
function ScanResultEmbarque({ result, onConfirmCheckIn, onNewScan, loading }) {
  const { reserva, cliente, viagem } = result;
  const jaFezCheckIn = reserva?.check_in;
  const cancelada = reserva?.status === 'cancelada';
  const data_fmt = viagem?.data ? new Date(viagem.data + 'T00:00:00').toLocaleDateString('pt-BR') : '';

  return (
    <div className="space-y-4 w-full max-w-md mx-auto">
      {jaFezCheckIn ? (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <XCircle className="w-8 h-8 text-amber-500 flex-shrink-0" />
          <div>
            <p className="font-bold text-amber-800">Check-in já realizado</p>
            <p className="text-amber-600 text-xs mt-0.5">
              Embarcou às {reserva.check_in_at ? new Date(reserva.check_in_at).toLocaleString('pt-BR') : '—'}
              {reserva.check_in_by ? ` por ${reserva.check_in_by}` : ''}
            </p>
          </div>
        </div>
      ) : cancelada ? (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
          <XCircle className="w-8 h-8 text-red-500 flex-shrink-0" />
          <p className="font-bold text-red-700">Reserva cancelada — não autorizado</p>
        </div>
      ) : (
        <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <CheckCircle className="w-8 h-8 text-emerald-500 flex-shrink-0" />
          <div>
            <p className="font-bold text-emerald-800">Passageiro encontrado!</p>
            <p className="text-emerald-600 text-xs mt-0.5">Pronto para confirmar embarque</p>
          </div>
        </div>
      )}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="bg-slate-900 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl bg-orange-500 flex flex-col items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-2xl leading-none">{reserva?.assento}</span>
              <span className="text-orange-200 text-[9px] leading-none mt-0.5 tracking-tight">ASSENTO</span>
            </div>
            <div>
              <p className="text-white font-bold">{cliente?.nome || '—'}</p>
              <p className="text-slate-400 text-sm">{cliente?.telefone || 'Sem telefone'}</p>
            </div>
          </div>
          <span className={`text-xs px-2 py-1 rounded-full font-bold ${jaFezCheckIn ? 'bg-emerald-500 text-white' : 'bg-orange-500 text-white'}`}>
            {jaFezCheckIn ? 'EMBARCADO' : (reserva?.status || '').toUpperCase()}
          </span>
        </div>
        <div className="p-4 space-y-2">
          <div className="flex items-center gap-2 text-slate-600 text-sm">
            <Bus className="w-4 h-4 text-orange-400 flex-shrink-0" />
            <span className="font-semibold">{viagem?.titulo || '—'}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <span>{viagem?.origem} → {viagem?.destino}</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <span>{data_fmt}</span>
            </div>
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Clock className="w-4 h-4 text-orange-400 flex-shrink-0" />
              <span className="font-semibold text-orange-600">{viagem?.horario}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-3">
        <button onClick={onNewScan}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border border-slate-300 text-slate-700 rounded-xl font-medium hover:bg-slate-50 transition-colors">
          <RefreshCw className="w-4 h-4" /> Novo Scan
        </button>
        {!jaFezCheckIn && !cancelada && (
          <button onClick={onConfirmCheckIn} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-bold transition-colors disabled:opacity-60">
            <CheckCircle className="w-5 h-5" />
            {loading ? 'Confirmando...' : 'Confirmar Embarque'}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Resultado scan — Encomenda ────────────────────────────────────────────
// Sistema Tour usa campos flat: remetente_nome, destinatario_nome, remetente_telefone, destinatario_telefone
function ScanResultEncomenda({ encomenda, viagem, onConfirmEntrega, onNewScan, loading }) {
  const [step, setStep] = useState('info'); // info | assinatura
  const [nomeRecebedor, setNomeRecebedor] = useState('');
  const jaEntregue = encomenda?.status === 'entregue';
  const cancelada = encomenda?.status === 'cancelada';
  const dataViagem = viagem?.data ? new Date(viagem.data + 'T00:00:00').toLocaleDateString('pt-BR') : '';

  return (
    <div className="space-y-4 w-full max-w-md mx-auto">
      {jaEntregue ? (
        <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <CheckCircle className="w-8 h-8 text-emerald-500 flex-shrink-0" />
          <div>
            <p className="font-bold text-emerald-800">Encomenda já entregue</p>
            <p className="text-emerald-600 text-xs mt-0.5">
              Entregue em {encomenda.data_entrega ? new Date(encomenda.data_entrega).toLocaleString('pt-BR') : '—'}
              {encomenda.entregue_por ? ` por ${encomenda.entregue_por}` : ''}
            </p>
          </div>
        </div>
      ) : cancelada ? (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
          <XCircle className="w-8 h-8 text-red-500 flex-shrink-0" />
          <p className="font-bold text-red-700">Encomenda cancelada — não pode ser entregue</p>
        </div>
      ) : (
        <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <Package className="w-8 h-8 text-emerald-500 flex-shrink-0" />
          <div>
            <p className="font-bold text-emerald-800">Encomenda encontrada!</p>
            <p className="text-emerald-600 text-xs mt-0.5">Pronta para confirmação de entrega</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="bg-slate-900 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center">
              <Package className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-white font-bold text-sm">{encomenda?.codigo_interno || '—'}</p>
              <p className="text-slate-400 text-xs">{encomenda?.volumes} volume(s)</p>
            </div>
          </div>
          <span className={`text-xs px-2 py-1 rounded-full font-bold ${
            jaEntregue ? 'bg-emerald-500 text-white' :
            cancelada ? 'bg-red-500 text-white' : 'bg-amber-500 text-white'}`}>
            {jaEntregue ? 'ENTREGUE' : cancelada ? 'CANCELADA' : (encomenda?.status || '').toUpperCase().replace('_', ' ')}
          </span>
        </div>
        <div className="p-4 space-y-2.5 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-slate-400 text-xs">Remetente</p>
              <p className="font-semibold text-slate-800">{encomenda?.remetente_nome || '—'}</p>
              <p className="text-xs text-slate-500">{encomenda?.remetente_telefone || ''}</p>
            </div>
            <div>
              <p className="text-slate-400 text-xs">Destinatário</p>
              <p className="font-semibold text-slate-800">{encomenda?.destinatario_nome || 'Não informado'}</p>
              <p className="text-xs text-slate-500">{encomenda?.destinatario_telefone || ''}</p>
            </div>
          </div>
          {(viagem?.origem || viagem?.destino) && (
            <div className="flex items-center gap-2 text-slate-600">
              <MapPin className="w-4 h-4 text-orange-400 flex-shrink-0" />
              <span>{viagem?.origem} → {viagem?.destino}</span>
            </div>
          )}
          {dataViagem && (
            <div className="flex items-center gap-2 text-slate-500">
              <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <span>{dataViagem}</span>
              {viagem?.horario && <span className="text-orange-600 font-semibold">· {viagem.horario}</span>}
            </div>
          )}
          {encomenda?.descricao && (
            <p className="text-slate-500 text-xs italic border-t border-slate-100 pt-2">{encomenda.descricao}</p>
          )}
        </div>
      </div>

      {!jaEntregue && !cancelada && (
        <>
          {step === 'info' && (
            <div className="space-y-3">
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1">Nome de quem recebeu (opcional)</label>
                <input value={nomeRecebedor} onChange={e => setNomeRecebedor(e.target.value)}
                  placeholder="Ex: João Silva"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
              </div>
              <div className="flex gap-3">
                <button onClick={onNewScan}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border border-slate-300 text-slate-700 rounded-xl font-medium hover:bg-slate-50 transition-colors">
                  <RefreshCw className="w-4 h-4" /> Cancelar
                </button>
                <button onClick={() => setStep('assinatura')}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-bold transition-colors">
                  <PenLine className="w-4 h-4" /> Confirmar Entrega
                </button>
              </div>
            </div>
          )}
          {step === 'assinatura' && (
            <div className="space-y-3">
              <SignatureCanvas onSave={(assinatura) => onConfirmEntrega(nomeRecebedor, assinatura)} onSkip={() => onConfirmEntrega(nomeRecebedor, null)} />
              {loading && (
                <div className="flex items-center justify-center gap-2 text-slate-500 text-sm py-2">
                  <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                  Confirmando entrega...
                </div>
              )}
            </div>
          )}
        </>
      )}

      {(jaEntregue || cancelada) && (
        <button onClick={onNewScan}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-slate-300 text-slate-700 rounded-xl font-medium hover:bg-slate-50 transition-colors">
          <RefreshCw className="w-4 h-4" /> Novo Scan
        </button>
      )}
    </div>
  );
}

// ─── Tela de sucesso ───────────────────────────────────────────────────────
function SuccessScreen({ title, subtitle, lines, btnLabel, onNext }) {
  return (
    <div className="flex flex-col items-center text-center space-y-5 w-full max-w-sm mx-auto py-6">
      <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center animate-bounce">
        <CheckCircle className="w-10 h-10 text-emerald-500" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h2>
        <p className="text-slate-500 text-sm mt-1">{subtitle}</p>
      </div>
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 w-full text-left space-y-1">
        {lines.map((l, i) => (
          <p key={i} className={`text-sm ${i === 0 ? 'font-bold text-emerald-800 text-base' : 'text-emerald-600'}`}>{l}</p>
        ))}
      </div>
      <button onClick={onNext}
        className="w-full bg-[#d72d21] hover:bg-[#b91c1c] text-white font-bold py-3 rounded-xl transition-colors">
        {btnLabel}
      </button>
    </div>
  );
}

// ─── Painel status embarque ────────────────────────────────────────────────
function CheckInPanel({ viagemId }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!viagemId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/viagens/${viagemId}/checkin-status`);
      setStatus(data);
    } catch (_) {}
    finally { setLoading(false); }
  }, [viagemId]);

  useEffect(() => { fetchStatus(); }, [viagemId, fetchStatus]);
  if (!viagemId || !status) return null;

  const pct = status.total > 0 ? Math.round((status.checkin_ok / status.total) * 100) : 0;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-slate-700 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>Status de Embarque</h3>
        <button onClick={fetchStatus} className="text-slate-400 hover:text-slate-600">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-3 text-center">
        <div className="bg-slate-50 rounded-lg p-2">
          <p className="text-xl font-bold text-slate-800">{status.total}</p>
          <p className="text-xs text-slate-500">Total</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-2">
          <p className="text-xl font-bold text-emerald-600">{status.checkin_ok}</p>
          <p className="text-xs text-emerald-500">Embarcados</p>
        </div>
        <div className="bg-amber-50 rounded-lg p-2">
          <p className="text-xl font-bold text-amber-600">{status.aguardando}</p>
          <p className="text-xs text-amber-500">Aguardando</p>
        </div>
      </div>
      <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
        <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-slate-500 mt-1 text-right">{pct}% embarcados</p>
      {status.passageiros?.length > 0 && (
        <div className="mt-3 space-y-1 max-h-48 overflow-y-auto">
          {status.passageiros.map((p) => (
            <div key={p.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs ${p.check_in ? 'bg-emerald-50' : 'bg-slate-50'}`}>
              <span className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold flex-shrink-0 ${p.check_in ? 'bg-emerald-500 text-white' : 'bg-orange-100 text-orange-600'}`}>
                {p.assento}
              </span>
              <span className="flex-1 font-medium text-slate-700 truncate">{p.cliente?.nome || '—'}</span>
              {p.check_in
                ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                : <Clock className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Busca por CPF ─────────────────────────────────────────────────────────
// Retorna lista [{reserva, cliente, viagem}] — mostra seletor se múltiplos resultados
function CpfBusca({ onFound, selectedViagem }) {
  const [cpf, setCpf] = useState('');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [opcoes, setOpcoes] = useState(null); // lista quando há múltiplos

  const mascarar = (v) => {
    const n = v.replace(/\D/g, '').slice(0, 11);
    if (n.length <= 3) return n;
    if (n.length <= 6) return `${n.slice(0, 3)}.${n.slice(3)}`;
    if (n.length <= 9) return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6)}`;
    return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6, 9)}.${n.slice(9, 11)}`;
  };

  const handleBuscar = async (e) => {
    e.preventDefault();
    if (cpf.replace(/\D/g, '').length < 9) { setErro('Informe pelo menos 9 dígitos do CPF'); return; }
    setLoading(true);
    setErro('');
    setOpcoes(null);
    try {
      const params = { cpf };
      if (selectedViagem) params.viagem_id = selectedViagem;
      const { data } = await api.get('/reservas/buscar-por-cpf', { params });
      if (data.length === 1) {
        onFound(data[0]);
      } else {
        setOpcoes(data);
      }
    } catch (err) {
      setErro(err.response?.data?.detail || 'Passageiro não encontrado.');
    } finally {
      setLoading(false);
    }
  };

  if (opcoes) {
    return (
      <div className="w-full space-y-2">
        <p className="text-xs font-semibold text-slate-500 text-center">Selecione a reserva correta:</p>
        {opcoes.map((item, i) => (
          <button key={i} onClick={() => { setOpcoes(null); onFound(item); }}
            className="w-full text-left p-3 border border-slate-200 rounded-xl hover:border-orange-400 hover:bg-orange-50 transition-colors">
            <p className="font-semibold text-slate-800 text-sm">{item.cliente?.nome || '—'} — Assento {item.reserva?.assento}</p>
            <p className="text-xs text-slate-500">{item.viagem?.titulo} · {item.viagem?.data}</p>
          </button>
        ))}
        <button onClick={() => setOpcoes(null)} className="w-full text-xs text-slate-400 hover:text-slate-600 py-1">
          Cancelar
        </button>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 h-px bg-slate-200" />
        <span className="text-xs text-slate-400 font-semibold px-2 uppercase tracking-wider">OU BUSCAR POR CPF</span>
        <div className="flex-1 h-px bg-slate-200" />
      </div>
      <form onSubmit={handleBuscar} className="space-y-2">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={cpf} onChange={e => { setCpf(mascarar(e.target.value)); setErro(''); }}
              placeholder="000.000.000-00" inputMode="numeric"
              className={`w-full pl-9 pr-3 py-2.5 border rounded-xl text-sm font-mono focus:outline-none focus:ring-2 transition-colors ${
                erro ? 'border-red-300 bg-red-50 focus:ring-red-200' : 'border-slate-300 focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)]'
              }`} />
          </div>
          <button type="submit" disabled={loading || cpf.replace(/\D/g, '').length < 9}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-[var(--brand-primary)] hover:opacity-90 text-white rounded-xl text-sm font-semibold disabled:opacity-50 transition-colors">
            {loading
              ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <><Search className="w-4 h-4" /> Buscar</>}
          </button>
        </div>
        {erro && (
          <div className="flex items-center gap-1.5 text-red-600 text-xs px-1">
            <XCircle className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{erro}</span>
          </div>
        )}
        <p className="text-xs text-slate-400 px-1">
          {selectedViagem ? 'Selecione a viagem acima para buscar apenas nela.' : 'Busca em todas as viagens.'}
        </p>
      </form>
    </div>
  );
}

// ─── Entrada manual de código QR ───────────────────────────────────────────
function ManualCodeEntry({ onSubmit, isEmbarque }) {
  const [expanded, setExpanded] = useState(false);
  const [code, setCode] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    const val = code.trim();
    if (!val) return;
    onSubmit(val);
    setCode('');
    setExpanded(false);
  };

  return (
    <div className="w-full">
      <button type="button" onClick={() => setExpanded(p => !p)}
        className="w-full flex items-center justify-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 py-2 transition-colors">
        <QrCode className="w-3.5 h-3.5" />
        {expanded ? 'Ocultar entrada manual' : 'Câmera não funciona? Digite o código manualmente'}
      </button>
      {expanded && (
        <form onSubmit={handleSubmit} className="mt-2 space-y-2 bg-slate-50 rounded-xl p-3 border border-slate-200">
          <p className="text-xs text-slate-500 font-medium">
            {isEmbarque ? 'Cole o código do QR Code do bilhete (ex: TURISMO-RES|...)' : 'Cole o código da etiqueta da encomenda (ex: TURISMO-CARGO|...)'}
          </p>
          <div className="flex gap-2">
            <input value={code} onChange={e => setCode(e.target.value)}
              placeholder={isEmbarque ? 'TURISMO-RES|...' : 'TURISMO-CARGO|...'}
              className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)] font-mono" />
            <button type="submit" disabled={!code.trim()}
              className="px-4 py-2 bg-[var(--brand-primary)] hover:opacity-90 text-white rounded-lg text-xs font-semibold disabled:opacity-50 transition-colors whitespace-nowrap">
              Verificar
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────────
export default function LeitoreQRCode() {
  const { branding } = useTenant();
  const showEncomendas = moduleEnabled(branding, 'encomendas');
  const [mode, setMode] = useState('embarque'); // embarque | encomendas
  const [viagens, setViagens] = useState([]);
  const [selectedViagem, setSelectedViagem] = useState('');
  const [stage, setStage] = useState('idle'); // idle | scanning | loading | result | success | error
  const [scanResult, setScanResult] = useState(null);
  const [successData, setSuccessData] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [scannerKey, setScannerKey] = useState(0);

  useEffect(() => {
    Promise.all([
      api.get('/viagens', { params: { status: 'agendada' } }),
      api.get('/viagens', { params: { status: 'em_andamento' } }),
    ]).then(([r1, r2]) => {
      const todas = [...(r1.data || []), ...(r2.data || [])];
      todas.sort((a, b) => (a.data || '').localeCompare(b.data || ''));
      setViagens(todas);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!showEncomendas && mode === 'encomendas') setMode('embarque');
  }, [showEncomendas, mode]);

  const switchMode = (m) => {
    setMode(m);
    setStage('idle');
    setScanResult(null);
    setSuccessData(null);
    setErrorMsg('');
  };

  // ─── Embarque ─────────────────────────────────────────
  const handleScanEmbarque = useCallback(async (decoded) => {
    setStage('loading');
    const timeoutId = setTimeout(() => {
      setErrorMsg('Tempo esgotado ao verificar QR Code. Verifique a conexão e tente novamente.');
      setStage('error');
    }, 12000);
    try {
      const parts = decoded.split('|');
      if (parts[0] !== 'TURISMO-RES' || !parts[1]) {
        clearTimeout(timeoutId);
        setErrorMsg('QR Code inválido para embarque. Certifique-se de usar o bilhete do passageiro.');
        setStage('error'); return;
      }
      const { data } = await api.get(`/reservas/scan/${parts[1]}`);
      clearTimeout(timeoutId);
      if (selectedViagem && data.reserva?.viagem_id !== selectedViagem) {
        setErrorMsg(`Este bilhete é de outra viagem: "${data.viagem?.titulo || ''}"`);
        setStage('error'); return;
      }
      setScanResult(data);
      setStage('result');
    } catch (err) {
      clearTimeout(timeoutId);
      setErrorMsg(err.response?.data?.detail || 'QR Code não reconhecido. Tente novamente.');
      setStage('error');
    }
  }, [selectedViagem]);

  const handleConfirmCheckIn = async () => {
    setActionLoading(true);
    try {
      await api.post(`/reservas/${scanResult.reserva.id}/check-in`);
      setSuccessData(scanResult); // usa dados já carregados do scan
      setStage('success');
      toast.success('Embarque confirmado!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao confirmar embarque');
    } finally {
      setActionLoading(false);
    }
  };

  // ─── Encomenda ────────────────────────────────────────
  const handleScanEncomenda = useCallback(async (decoded) => {
    setStage('loading');
    const timeoutId = setTimeout(() => {
      setErrorMsg('Tempo esgotado. Verifique a conexão e tente novamente.');
      setStage('error');
    }, 12000);
    try {
      const parts = decoded.split('|');
      if (parts[0] !== 'TURISMO-CARGO' || !parts[1]) {
        clearTimeout(timeoutId);
        setErrorMsg('QR Code inválido para encomenda. Use a etiqueta gerada pelo sistema.');
        setStage('error'); return;
      }
      const { data } = await api.get(`/encomendas/scan/${encodeURIComponent(decoded)}`);
      clearTimeout(timeoutId);
      setScanResult(data); // {encomenda, viagem}
      setStage('result');
    } catch (err) {
      clearTimeout(timeoutId);
      setErrorMsg(err.response?.data?.detail || 'Encomenda não encontrada.');
      setStage('error');
    }
  }, []);

  const handleConfirmEntrega = async (nomeRecebedor, assinatura) => {
    setActionLoading(true);
    try {
      await api.post(`/encomendas/${scanResult.encomenda.id}/confirmar-entrega`, {
        nome_recebedor: nomeRecebedor || null,
        assinatura: assinatura || null,
      });
      const enc = scanResult.encomenda;
      const viag = scanResult.viagem;
      setSuccessData({
        codigo: enc?.codigo_interno,
        remetente: enc?.remetente_nome,
        destinatario: enc?.destinatario_nome,
        rota: viag ? `${viag.origem} → ${viag.destino}` : '—',
        nomeRecebedor,
      });
      setStage('success');
      toast.success('Entrega confirmada!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao confirmar entrega');
    } finally {
      setActionLoading(false);
    }
  };

  const handleScan = mode === 'embarque' ? handleScanEmbarque : handleScanEncomenda;

  const handleCpfFound = useCallback((data) => {
    setScanResult(data);
    setStage('result');
  }, []);

  const handleNewScan = () => {
    setScanResult(null);
    setSuccessData(null);
    setErrorMsg('');
    setScannerKey(k => k + 1);
    setStage('scanning');
  };

  const handleReset = () => {
    setScanResult(null);
    setSuccessData(null);
    setErrorMsg('');
    setScannerKey(k => k + 1);
    setStage('idle');
  };

  const isEmbarque = mode === 'embarque';

  return (
    <div className="max-w-2xl mx-auto space-y-5" data-testid="leitor-qrcode-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Leitor QR Code
        </h1>
        <p className="text-slate-500 text-sm mt-1">Escaneie QR Codes para confirmar embarques ou entregas de encomendas.</p>
      </div>

      {/* Abas de modo */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
        <button onClick={() => switchMode('embarque')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
            isEmbarque ? 'bg-white shadow-sm text-orange-600 border border-orange-100' : 'text-slate-500 hover:text-slate-700'
          }`}>
          <Users className="w-4 h-4" /> Embarque Passageiros
        </button>
        {showEncomendas && (
          <button onClick={() => switchMode('encomendas')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
              !isEmbarque ? 'bg-white shadow-sm text-emerald-600 border border-emerald-100' : 'text-slate-500 hover:text-slate-700'
            }`}>
            <Package className="w-4 h-4" /> Entrega de Encomendas
          </button>
        )}
      </div>

      {/* Filtro de viagem (apenas embarque) */}
      {isEmbarque && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <label className="block text-sm font-semibold text-slate-700 mb-2">Viagem sendo embarcada (opcional)</label>
          <select value={selectedViagem} onChange={(e) => setSelectedViagem(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 bg-white">
            <option value="">Todas as viagens (sem validação)</option>
            {viagens.map(v => (
              <option key={v.id} value={v.id}>
                {v.titulo} — {v.origem} → {v.destino} — {v.data ? new Date(v.data + 'T00:00:00').toLocaleDateString('pt-BR') : ''} {v.horario}
              </option>
            ))}
          </select>
          <p className="text-xs text-slate-400 mt-1.5">Bilhetes de outras viagens serão recusados automaticamente.</p>
        </div>
      )}

      {/* Área principal */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">

        {/* IDLE */}
        {stage === 'idle' && (
          <div className="flex flex-col items-center py-8 text-center space-y-5">
            <div className={`w-20 h-20 rounded-2xl flex items-center justify-center ${isEmbarque ? 'bg-orange-50' : 'bg-emerald-50'}`}>
              {isEmbarque
                ? <QrCode className="w-10 h-10 text-orange-500" />
                : <Package className="w-10 h-10 text-emerald-500" />}
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {isEmbarque ? 'Pronto para escanear bilhetes' : 'Pronto para escanear etiquetas'}
              </h2>
              <p className="text-slate-500 text-sm mt-1">
                {isEmbarque
                  ? 'Clique para abrir a câmera e escanear o QR Code do bilhete do passageiro'
                  : 'Clique para abrir a câmera e escanear a etiqueta da encomenda'}
              </p>
            </div>
            <button onClick={() => setStage('scanning')}
              className={`flex items-center gap-2 text-white font-bold px-8 py-3.5 rounded-xl transition-colors active:scale-95 ${isEmbarque ? 'bg-[#d72d21] hover:bg-[#b91c1c]' : 'bg-emerald-500 hover:bg-emerald-600'}`}>
              <Camera className="w-5 h-5" />
              Abrir Câmera
            </button>
            {isEmbarque && (
              <div className="w-full max-w-sm">
                <CpfBusca onFound={handleCpfFound} selectedViagem={selectedViagem} />
              </div>
            )}
          </div>
        )}

        {/* SCANNING */}
        {stage === 'scanning' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-slate-700">Escaneando QR Code...</h2>
              <button onClick={handleReset} className="text-slate-400 hover:text-slate-600 text-sm underline">Cancelar</button>
            </div>
            <QrScanner
              key={scannerKey}
              onScan={handleScan}
              onError={(msg) => { setErrorMsg(`Câmera indisponível: ${msg}`); setStage('error'); }}
              hintText={isEmbarque ? 'Aponte para o QR Code do bilhete do passageiro' : 'Aponte para a etiqueta da encomenda'}
            />
            <ManualCodeEntry onSubmit={handleScan} isEmbarque={isEmbarque} />
            {isEmbarque && <CpfBusca onFound={handleCpfFound} selectedViagem={selectedViagem} />}
          </div>
        )}

        {/* LOADING */}
        {stage === 'loading' && (
          <div className="flex flex-col items-center py-12 space-y-3">
            <div className="w-12 h-12 rounded-full border-4 border-orange-200 border-t-orange-500 animate-spin" />
            <p className="text-slate-500 text-sm">{isEmbarque ? 'Verificando passageiro...' : 'Verificando encomenda...'}</p>
          </div>
        )}

        {/* RESULT — EMBARQUE */}
        {stage === 'result' && scanResult && isEmbarque && (
          <ScanResultEmbarque
            result={scanResult}
            onConfirmCheckIn={handleConfirmCheckIn}
            onNewScan={handleNewScan}
            loading={actionLoading}
          />
        )}

        {/* RESULT — ENCOMENDA */}
        {stage === 'result' && scanResult && !isEmbarque && (
          <ScanResultEncomenda
            encomenda={scanResult.encomenda}
            viagem={scanResult.viagem}
            onConfirmEntrega={handleConfirmEntrega}
            onNewScan={handleNewScan}
            loading={actionLoading}
          />
        )}

        {/* SUCCESS — EMBARQUE */}
        {stage === 'success' && successData && isEmbarque && (
          <SuccessScreen
            title="Embarque Confirmado!"
            subtitle="Passageiro autorizado a embarcar"
            lines={[
              successData.cliente?.nome,
              `Assento #${successData.reserva?.assento}`,
              `${successData.viagem?.origem} → ${successData.viagem?.destino}`,
              `${successData.viagem?.horario}h — ${successData.viagem?.data ? new Date(successData.viagem.data + 'T00:00:00').toLocaleDateString('pt-BR') : ''}`,
            ].filter(Boolean)}
            btnLabel="Próximo Passageiro"
            onNext={handleNewScan}
          />
        )}

        {/* SUCCESS — ENCOMENDA */}
        {stage === 'success' && successData && !isEmbarque && (
          <SuccessScreen
            title="Entrega Confirmada!"
            subtitle="Encomenda entregue com sucesso"
            lines={[
              `Código: ${successData.codigo}`,
              `Remetente: ${successData.remetente}`,
              ...(successData.destinatario ? [`Destinatário: ${successData.destinatario}`] : []),
              `Rota: ${successData.rota}`,
              ...(successData.nomeRecebedor ? [`Recebeu: ${successData.nomeRecebedor}`] : []),
            ].filter(Boolean)}
            btnLabel="Próxima Encomenda"
            onNext={handleNewScan}
          />
        )}

        {/* ERROR */}
        {stage === 'error' && (
          <div className="flex flex-col items-center py-6 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
            <div>
              <h2 className="font-bold text-slate-800 text-lg">Erro ao processar QR Code</h2>
              <p className="text-red-500 text-sm mt-1 max-w-xs">{errorMsg}</p>
            </div>
            <button onClick={handleNewScan}
              className="flex items-center gap-2 bg-[#d72d21] hover:bg-[#b91c1c] text-white font-bold px-6 py-2.5 rounded-xl transition-colors">
              <RefreshCw className="w-4 h-4" /> Tentar Novamente
            </button>
            {isEmbarque && (
              <div className="w-full pt-2">
                <CpfBusca onFound={handleCpfFound} selectedViagem={selectedViagem} />
              </div>
            )}
            <div className="w-full pt-1">
              <ManualCodeEntry onSubmit={handleScan} isEmbarque={isEmbarque} />
            </div>
          </div>
        )}
      </div>

      {/* Painel de status de embarque */}
      {isEmbarque && selectedViagem && (
        <CheckInPanel key={selectedViagem + stage} viagemId={selectedViagem} />
      )}
    </div>
  );
}
